import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { signOut } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { COLORS } from '../theme';

const NAV = [
  { label: 'Dashboard',     path: '/dashboard',     icon: '📊', matchFn: p => p === '/dashboard' },
  { label: 'Employers',     path: '/employers',     icon: '🏢', matchFn: p => p === '/employers' || p.startsWith('/employers/') },
  { label: 'Workers',       path: '/workers',       icon: '👷', matchFn: p => p === '/workers'   || p.startsWith('/workers/') },
  { label: 'Ambassadors',       path: '/ambassadors',       icon: '🤝', matchFn: p => p === '/ambassadors' || p.startsWith('/ambassadors/') },
  { label: 'Referral Reviews',  path: '/referral-reviews',  icon: '📋', matchFn: p => p === '/referral-reviews' },
  { label: 'Rating Reviews',    path: '/rating-consistency-reviews', icon: '⚖️', matchFn: p => p === '/rating-consistency-reviews' },
  { label: 'Content Reports',   path: '/content-reports',   icon: '🚩', matchFn: p => p === '/content-reports' },
  { label: 'Revenue',           path: '/revenue',           icon: '💰', matchFn: p => p === '/revenue' },
  { label: 'Feature Flags', path: '/feature-flags', icon: '⚙️', matchFn: p => p === '/feature-flags' },
  { label: 'Cities',        path: '/cities',         icon: '🏙️', matchFn: p => p === '/cities' },
  { label: 'Onboarding Q\'s', path: '/onboarding-questions', icon: '❓', matchFn: p => p === '/onboarding-questions' },
  { label: 'Guest Preview',  path: '/guest-preview', icon: '🎬', matchFn: p => p === '/guest-preview' },
];

function getPageTitle(pathname) {
  if (pathname.startsWith('/employers/')) return 'Employer Detail';
  if (pathname.startsWith('/workers/')) return 'Worker Detail';
  if (pathname.startsWith('/ambassadors/')) return 'Ambassador Detail';
  // HC-SECURITY-007 (Prompt #329): reached via the gear icon next to the
  // SUPERADMIN badge below, not a sidebar NAV entry — needs its own title
  // case since it won't match anything in NAV.
  if (pathname === '/security') return 'Security';
  const item = NAV.find(n => n.matchFn(pathname));
  return item ? item.label : 'Super Admin';
}

export default function AppShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, claims } = useAuth();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [pendingConsistencyReviewCount, setPendingConsistencyReviewCount] = useState(0);
  const [pendingContentReportCount, setPendingContentReportCount] = useState(0);
  // PROMPT #310: same derivation as mobile's Profile screen support-code
  // display (mobile/lib/features/profile/utils/version_support_line.dart) —
  // the raw Firebase Auth uid, labeled "Support Code" rather than "UID".
  const [supportCodeCopied, setSupportCodeCopied] = useState(false);
  // HC-SECURITY-007 follow-up (Prompt #330): AuthContext only carries
  // role/accessLevel claims (checked directly, not assumed) — firstName/
  // lastName aren't available there, so this is a small, independent,
  // one-shot read of the same users/{uid} fields SecurityPage.js already
  // reads/writes. Deliberately not added to AuthContext itself: nothing
  // else in the app needs the name today, and AppShell already reads
  // Firestore directly elsewhere in this same file (the 3 badge-count
  // listeners below), so this follows an existing pattern rather than
  // introducing a new one.
  const [fullName, setFullName] = useState('');

  useEffect(() => {
    if (!user?.uid) {
      setFullName('');
      return;
    }
    let cancelled = false;
    getDoc(doc(db, 'users', user.uid))
      .then(snap => {
        if (cancelled) return;
        const data = snap.exists() ? snap.data() : {};
        const name = [data.firstName, data.lastName].filter(Boolean).join(' ').trim();
        setFullName(name);
      })
      .catch(() => {
        if (!cancelled) setFullName('');
      });
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'ambassador_referrals'), where('reviewStatus', '==', 'pending')),
      snap => setPendingReviewCount(snap.size)
    );
    return () => unsub();
  }, []);

  // HC-RATING-004 follow-on: mirrors the ambassador_referrals badge above
  // exactly — a second, independent lightweight onSnapshot purely for the
  // nav count, separate from RatingConsistencyReviewsPage's own listener.
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'ratingConsistencyReviews'), where('reviewStatus', '==', 'pending')),
      snap => setPendingConsistencyReviewCount(snap.size)
    );
    return () => unsub();
  }, []);

  // HC-REPORT-001 (Apple 1.2, Prompt #278): same pattern again — independent
  // nav-count listener, separate from ContentReportsPage's own.
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'contentReports'), where('status', '==', 'pending')),
      snap => setPendingContentReportCount(snap.size)
    );
    return () => unsub();
  }, []);

  async function handleSignOut() {
    await signOut();
    navigate('/login');
  }

  function handleCopySupportCode() {
    navigator.clipboard.writeText(user.uid).then(() => {
      setSupportCodeCopied(true);
      setTimeout(() => setSupportCodeCopied(false), 2000);
    });
  }

  const pageTitle = getPageTitle(location.pathname);
  // Graceful fallback per Prompt #330: empty/missing firstName+lastName
  // falls back to the Auth email rather than showing nothing or crashing;
  // if neither is available, the element simply isn't rendered at all.
  const displayIdentity = fullName || user?.email || '';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: COLORS.bg }}>

      {/* Sidebar */}
      <div style={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: COLORS.sidebarWidth,
        height: '100vh',
        backgroundColor: COLORS.sidebar,
        borderRight: `1px solid ${COLORS.border}`,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 100,
      }}>

        {/* Wordmark */}
        <div style={{
          padding: '20px 16px',
          borderBottom: `1px solid ${COLORS.border}`,
          flexShrink: 0,
        }}>
          <div style={{
            fontSize: '16px',
            fontWeight: '700',
            color: COLORS.accent,
            letterSpacing: '0.5px',
            lineHeight: '1.2',
          }}>
            ⚡ WorkrMatch
          </div>
          <div style={{
            fontSize: '11px',
            color: COLORS.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginTop: '2px',
          }}>
            Super Admin
          </div>
        </div>

        {/* Nav */}
        <div style={{ flex: 1, padding: '8px 0' }}>
          {NAV.map(item => {
            const isActive = item.matchFn(location.pathname);
            const hovered = hoveredItem === item.path;
            return (
              <div
                key={item.path}
                onClick={() => navigate(item.path)}
                onMouseEnter={() => setHoveredItem(item.path)}
                onMouseLeave={() => setHoveredItem(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 16px',
                  margin: '2px 8px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: isActive ? '600' : '400',
                  color: isActive ? COLORS.accent : hovered ? COLORS.textPrimary : COLORS.textSecondary,
                  backgroundColor: isActive ? COLORS.accent + '26' : hovered ? COLORS.surfaceHover : 'transparent',
                  borderLeft: isActive ? `3px solid ${COLORS.accent}` : '3px solid transparent',
                  transition: 'background-color 0.12s, color 0.12s',
                }}
              >
                <span style={{ fontSize: '15px', lineHeight: 1 }}>{item.icon}</span>
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.path === '/referral-reviews' && pendingReviewCount > 0 && (
                  <span style={{
                    backgroundColor: COLORS.danger,
                    color: 'white',
                    borderRadius: '10px',
                    fontSize: '10px',
                    fontWeight: '700',
                    padding: '1px 6px',
                    lineHeight: '16px',
                  }}>
                    {pendingReviewCount}
                  </span>
                )}
                {item.path === '/rating-consistency-reviews' && pendingConsistencyReviewCount > 0 && (
                  <span style={{
                    backgroundColor: COLORS.danger,
                    color: 'white',
                    borderRadius: '10px',
                    fontSize: '10px',
                    fontWeight: '700',
                    padding: '1px 6px',
                    lineHeight: '16px',
                  }}>
                    {pendingConsistencyReviewCount}
                  </span>
                )}
                {item.path === '/content-reports' && pendingContentReportCount > 0 && (
                  <span style={{
                    backgroundColor: COLORS.danger,
                    color: 'white',
                    borderRadius: '10px',
                    fontSize: '10px',
                    fontWeight: '700',
                    padding: '1px 6px',
                    lineHeight: '16px',
                  }}>
                    {pendingContentReportCount}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom: email + sign out */}
        <div style={{
          borderTop: `1px solid ${COLORS.border}`,
          padding: '16px',
          flexShrink: 0,
        }}>
          <div style={{
            fontSize: '12px',
            color: COLORS.textMuted,
            marginBottom: '8px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {user?.email || ''}
          </div>
          {user?.uid && (
            <div
              onClick={handleCopySupportCode}
              title="Click to copy"
              style={{
                fontSize: '11px',
                color: COLORS.textMuted,
                marginBottom: '8px',
                cursor: 'pointer',
                fontFamily: 'monospace',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {supportCodeCopied ? 'Copied!' : `Support Code: ${user.uid}`}
            </div>
          )}
          <span
            onClick={handleSignOut}
            style={{
              fontSize: '12px',
              color: COLORS.danger,
              cursor: 'pointer',
              fontWeight: '500',
            }}
            onMouseEnter={e => e.target.style.textDecoration = 'underline'}
            onMouseLeave={e => e.target.style.textDecoration = 'none'}
          >
            Sign Out
          </span>
        </div>
      </div>

      {/* Main content */}
      <div style={{
        marginLeft: COLORS.sidebarWidth,
        flex: 1,
        minHeight: '100vh',
        backgroundColor: COLORS.bg,
        padding: '32px',
        boxSizing: 'border-box',
      }}>

        {/* Topbar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${COLORS.border}`,
          paddingBottom: '16px',
          marginBottom: '24px',
        }}>
          <span style={{
            fontSize: '20px',
            fontWeight: '600',
            color: COLORS.textPrimary,
          }}>
            {pageTitle}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* HC-SECURITY-007 follow-up (Prompt #330): per Clarence's
                screenshot feedback, the signed-in user's name should appear
                in the header, not just on the Security page. */}
            {/* PROMPT #333: bumped to 700 (unambiguous bold — matches how
                the wordmark above uses 700, and reads clearly bolder than
                the SUPERADMIN badge's own 600) and a generic person icon
                added before the name, per Clarence's direct feedback.
                Confirmed directly first (Rule 34): this span was already
                fontWeight 600 on this branch, matching the badge below —
                not "regular weight" as literally described — but the
                perceived-not-bold-enough complaint is addressed either way
                by going to genuine bold (700) rather than leaving it at a
                value already equal to the badge's own. */}
            {displayIdentity && (
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '13px',
                fontWeight: '700',
                color: COLORS.textPrimary,
              }}>
                {/* Same icon convention as the ⚙️ Security icon below —
                    a plain Unicode emoji sized via inline fontSize, not an
                    icon library (this file has none) or a custom asset. */}
                <span style={{ fontSize: '14px' }}>👤</span>
                {displayIdentity}
              </span>
            )}
            {claims.accessLevel && (
              <span style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: COLORS.accent,
                backgroundColor: COLORS.surface,
                padding: '4px 10px',
                borderRadius: '4px',
                fontWeight: '600',
              }}>
                {claims.accessLevel}
              </span>
            )}
            {/* HC-SECURITY-007 (Prompt #329): minimal Security entry point —
                Tammy's confirmed design is a name label + gear icon here,
                not a sidebar nav item. Opens the new profile+MFA page. */}
            <span
              onClick={() => navigate('/security')}
              title="Security"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                color: location.pathname === '/security' ? COLORS.accent : COLORS.textSecondary,
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = COLORS.surfaceHover}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <span style={{ fontSize: '14px' }}>⚙️</span>
              Security
            </span>
          </div>
        </div>

        {children}
      </div>
    </div>
  );
}
